import React, { Component } from "react";
import { ValidationGroup, Validators } from "util/validation";
import Form, { FormStatus } from "components/forms/Form";
import { Button, Typography, MenuItem } from "@material-ui/core";
import ProgressSpinner from "components/ProgressSpinner";
import "./index.scss";
import TextField from "components/forms/TextField";
import PhoneNumberField from "components/forms/PhoneNumberField";
import { BookingEnquiryUser } from "services/entities/contact";
import SelectField from "components/forms/SelectField";
import { LocationIcon } from "components/Icon";
import { Space, SpaceStatus } from "services/entities/spaces";
import { isLoggedIn } from "services/auth";

export interface UserModel {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  space?: string;
}

interface UserFormState {
  status: FormStatus;
}

interface UserFormProps {
  onSaved: (user: BookingEnquiryUser) => void;
  spaces?: Space[] | null;
  space?: any;
  changeSpace?: any;
}

export default class UserForm extends Component<UserFormProps, UserFormState> {
  private readonly validation: ValidationGroup;
  private readonly model: UserModel;
  public state: UserFormState = {
    status: FormStatus.None,
  };

  constructor(props: any) {
    super(props);
    this.model = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      companyName: "",
      space: "",
    };
    this.validation = new ValidationGroup(this.model);
    if (isLoggedIn()) {
      this.validation.addField("space", { required: Validators.required });
    } else {
      this.validation.addField("space", { required: Validators.required });
      this.validation.addField("firstName", { required: Validators.required });
      this.validation.addField("lastName", { required: Validators.required });
      this.validation.addField("email", {
        required: Validators.required,
        format: Validators.email,
      });
      this.validation.addField("phone", {
        required: Validators.required,
        format: Validators.phone,
      });
      this.validation.addField("companyName");
    }
  }

  componentDidMount() {
    this.model.space = this.props.space || "";
  }

  onSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    this.props.onSaved(this.model as BookingEnquiryUser);
  };

  onFieldInput = () => {
    this.setState({ status: FormStatus.None });
  };

  onSpaceFieldInput = () => {
    this.props.changeSpace();
    this.onFieldInput();
  };

  render() {
    const disabled = this.state.status === FormStatus.Submitting;
    return (
      <Form
        className="booking-enquiry-user-form"
        onSubmit={this.onSubmit}
        validationGroup={this.validation}
      >
        <h2 className="title">Check Availability</h2>
        <Typography variant="body1" component="p" className="text">
          Please fill in your details below, and someone in our team will reach
          out to you.
        </Typography>
        {this.props.spaces && (
          <SelectField
            name="space"
            placeholder="Select a location"
            model={this.model}
            validationGroup={this.validation}
            onChange={this.onSpaceFieldInput}
            validationMessages={{ required: "Location is required" }}
            menuProps={{
              getContentAnchorEl: null,
              anchorOrigin: { horizontal: "left", vertical: "bottom" },
              transformOrigin: { horizontal: "left", vertical: "top" },
            }}
            startAdornment={<LocationIcon />}
          >
            {this.props.spaces &&
              this.props.spaces.map((space, index) => (
                <MenuItem
                  key={index}
                  value={space.code}
                  disabled={
                    !space.bookingEnabled ||
                    space.status !== SpaceStatus.Available
                  }
                >
                  {space.name}
                  {space.status !== SpaceStatus.Available
                    ? " (coming soon)"
                    : ""}
                </MenuItem>
              ))}
          </SelectField>
        )}
        {!isLoggedIn() && (
          <>
            <div className="section">
              <TextField
                name="firstName"
                label="First name"
                type="text"
                className="first-name"
                required={true}
                validationGroup={this.validation}
                model={this.model}
                validationMessages={{ required: "First name is required" }}
                disabled={disabled}
                onInput={this.onFieldInput}
              />
              <TextField
                name="lastName"
                label="Last name"
                type="text"
                className="last-name"
                required={true}
                validationGroup={this.validation}
                model={this.model}
                validationMessages={{ required: "Last name is required" }}
                disabled={disabled}
                onInput={this.onFieldInput}
              />
            </div>
            <div className="section">
              <TextField
                name="email"
                label="Email"
                className="email"
                type="text"
                required={true}
                validationGroup={this.validation}
                model={this.model}
                validationMessages={{
                  required: "Email is required",
                  format: "Invalid email",
                }}
                disabled={disabled}
                onInput={this.onFieldInput}
              />
              <PhoneNumberField
                name="phone"
                label="Phone"
                required={true}
                validationGroup={this.validation}
                model={this.model}
                validationMessages={{
                  required: "Phone number is required",
                  format: "Invalid phone number",
                }}
                disabled={disabled}
                onInput={this.onFieldInput}
              />
            </div>
            <TextField
              name="companyName"
              label="Company name"
              type="text"
              className="company-name"
              validationGroup={this.validation}
              model={this.model}
              validationMessages={{ required: "Company name is required" }}
              disabled={disabled}
              onInput={this.onFieldInput}
            />
          </>
        )}
        <Button
          variant="contained"
          className="submit"
          type="submit"
          disabled={disabled}
        >
          {this.state.status === FormStatus.Submitting && <ProgressSpinner />}
          Check Availability
        </Button>
      </Form>
    );
  }
}
